import React, { useState, useEffect } from 'react';
import { Box, Paper, TextField, Button, Typography, Alert, IconButton, Tooltip, Link, Collapse } from '@mui/material';
import { LightMode, DarkMode } from '@mui/icons-material';
import BrandIcon from '../components/BrandIcon';
import { useThemeMode } from '../contexts/ThemeContext';
import { useHubAuth } from '../contexts/HubAuthContext';
import axios from 'axios';

const HUB_API = process.env.REACT_APP_HUB_API_URL || '';

const HubLogin: React.FC = () => {
  const { login } = useHubAuth();
  const { mode, toggleTheme } = useThemeMode();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hubName, setHubName] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    const HUB_API = process.env.REACT_APP_HUB_API_URL || '';
    fetch(`${HUB_API}/api/v1/settings/public`)
      .then(r => r.json())
      .then(d => { setHubName(d.hub_name || ''); })
      .catch(() => {});
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post<{ access_token: string }>(`${HUB_API}/api/v1/auth/login`, { email, password });
      login(resp.data.access_token);
    } catch {
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    try {
      await axios.post(`${HUB_API}/api/v1/auth/forgot-password`, { email: forgotUsername });
    } catch {
    } finally {
      setForgotLoading(false);
      setForgotMessage('If that account exists, its credentials have been reset to the original setup values.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', position: 'relative' }}>
      <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
        {/* Login has no HubHeader, so nothing here reserves space for the
            Window Controls Overlay buttons - push this below that strip
            (0px fallback when WCO isn't active, so nothing shifts there). */}
        <IconButton onClick={toggleTheme} sx={{ position: 'absolute', top: 'calc(env(titlebar-area-height, 0px) + 16px)', right: 16 }}>
          {mode === 'dark' ? <LightMode /> : <DarkMode />}
        </IconButton>
      </Tooltip>
      <Paper sx={{ p: 4, width: 380 }} elevation={3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
          <BrandIcon color="primary" />
          <Box sx={{ textAlign: 'center' }}>
            {hubName && (
              <Typography variant="h6" fontWeight="bold">{hubName}</Typography>
            )}
            <Typography variant="h6" fontWeight="bold">RT Radiology Hub</Typography>
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleLogin}>
          <TextField fullWidth label="Username" value={email} onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }} size="small" type="text" required />
          <TextField fullWidth label="Password" value={password} onChange={e => setPassword(e.target.value)} sx={{ mb: 3 }} size="small" type="password" required />
          <Button fullWidth variant="contained" type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
          <Link component="button" variant="body2" underline="hover"
            onClick={() => { setForgotOpen(p => !p); setForgotMessage(''); }}>
            Forgot password?
          </Link>
        </Box>

        <Collapse in={forgotOpen}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            {forgotMessage ? (
              <Alert severity="info" sx={{ mb: 1 }}>{forgotMessage}</Alert>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Enter your username to reset your credentials back to the ones set during deployment.
                </Typography>
                <TextField fullWidth label="Username" value={forgotUsername}
                  onChange={e => setForgotUsername(e.target.value)} sx={{ mb: 1.5 }} size="small" type="text" required />
                <Button fullWidth variant="outlined" type="submit" disabled={forgotLoading}>
                  {forgotLoading ? 'Sending...' : 'Reset'}
                </Button>
              </form>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default HubLogin;
